import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Form,
  Select,
  DatePicker,
  Input,
  Button,
  Space,
  Tag,
  message,
  Descriptions,
  Modal,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import api from '../../utils/api';
import dayjs, { Dayjs } from 'dayjs';
import { formatBrisbaneTime } from '../../utils/time';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface AuditLog {
  id: number;
  action_type: string;
  user_id: number | null;
  user_email: string | null;
  resource_type: string | null;
  resource_id: number | null;
  description: string;
  details: any;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

const AuditLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [form] = Form.useForm();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  useEffect(() => {
    fetchLogs();
  }, [page, pageSize]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const values = form.getFieldsValue();
      const params: any = {
        page,
        page_size: pageSize,
      };

      if (values.action_type) params.action_type = values.action_type;
      if (values.user_id) params.user_id = values.user_id;
      if (values.user_email) params.user_email = values.user_email;
      if (values.resource_type) params.resource_type = values.resource_type;
      if (values.resource_id) params.resource_id = values.resource_id;
      if (values.dateRange) {
        params.start_date = values.dateRange[0].toISOString();
        params.end_date = values.dateRange[1].toISOString();
      }

      const response = await api.get('/admin/audit-logs', { params });
      setLogs(response.data.items);
      setTotal(response.data.total);
    } catch (error: any) {
      message.error('Failed to fetch audit logs: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchLogs();
  };

  const handleReset = () => {
    form.resetFields();
    setPage(1);
    fetchLogs();
  };

  const handleViewDetail = (log: AuditLog) => {
    setSelectedLog(log);
    setDetailModalVisible(true);
  };

  const getActionTypeColor = (actionType: string) => {
    if (actionType.includes('login')) return 'green';
    if (actionType.includes('logout')) return 'blue';
    if (actionType.includes('failed')) return 'red';
    if (actionType.includes('created')) return 'cyan';
    if (actionType.includes('updated')) return 'orange';
    if (actionType.includes('deleted')) return 'red';
    if (actionType.includes('sync')) return 'purple';
    return 'default';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Action Type',
      dataIndex: 'action_type',
      key: 'action_type',
      width: 150,
      render: (text: string) => (
        <Tag color={getActionTypeColor(text)}>{text}</Tag>
      ),
    },
    {
      title: 'User',
      dataIndex: 'user_email',
      key: 'user_email',
      width: 200,
      render: (text: string | null) => text || '-',
    },
    {
      title: 'Resource Type',
      dataIndex: 'resource_type',
      key: 'resource_type',
      width: 120,
      render: (text: string | null) => text || '-',
    },
    {
      title: 'Resource ID',
      dataIndex: 'resource_id',
      key: 'resource_id',
      width: 100,
      render: (text: number | null) => text || '-',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 150,
      render: (text: string | null) => text || '-',
    },
    {
      title: 'Time (Brisbane)',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => formatBrisbaneTime(text),
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      render: (_: any, record: AuditLog) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          Details
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Card title="Audit Logs" style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="action_type" label="Action Type">
            <Select style={{ width: 150 }} allowClear placeholder="All">
              <Option value="login">Login</Option>
              <Option value="logout">Logout</Option>
              <Option value="login_failed">Login Failed</Option>
              <Option value="user_created">User Created</Option>
              <Option value="user_updated">User Updated</Option>
              <Option value="user_deleted">User Deleted</Option>
              <Option value="store_created">Store Created</Option>
              <Option value="store_updated">Store Updated</Option>
              <Option value="store_deleted">Store Deleted</Option>
              <Option value="region_created">Region Created</Option>
              <Option value="region_updated">Region Updated</Option>
              <Option value="region_deleted">Region Deleted</Option>
              <Option value="sync_completed">Sync Completed</Option>
              <Option value="sync_failed">Sync Failed</Option>
            </Select>
          </Form.Item>
          <Form.Item name="user_email" label="User Email">
            <Input style={{ width: 200 }} placeholder="Enter email" allowClear />
          </Form.Item>
          <Form.Item name="resource_type" label="Resource Type">
            <Select style={{ width: 120 }} allowClear placeholder="All">
              <Option value="user">User</Option>
              <Option value="store">Store</Option>
              <Option value="region">Region</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange" label="Date Range">
            <RangePicker showTime />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                Search
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Reset
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <Table
          columns={columns}
          dataSource={logs}
          loading={loading}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
          }}
        />
      </Card>

      <Modal
        title="Audit Log Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedLog && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="ID">{selectedLog.id}</Descriptions.Item>
            <Descriptions.Item label="Action Type">
              <Tag color={getActionTypeColor(selectedLog.action_type)}>
                {selectedLog.action_type}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="User ID">{selectedLog.user_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="User Email">{selectedLog.user_email || '-'}</Descriptions.Item>
            <Descriptions.Item label="Resource Type">{selectedLog.resource_type || '-'}</Descriptions.Item>
            <Descriptions.Item label="Resource ID">{selectedLog.resource_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="Description">{selectedLog.description}</Descriptions.Item>
            <Descriptions.Item label="IP Address">{selectedLog.ip_address || '-'}</Descriptions.Item>
            <Descriptions.Item label="User Agent">{selectedLog.user_agent || '-'}</Descriptions.Item>
            <Descriptions.Item label="Time (Brisbane)">
              {formatBrisbaneTime(selectedLog.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Details">
              <pre style={{ maxHeight: 300, overflow: 'auto' }}>
                {JSON.stringify(selectedLog.details, null, 2)}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default AuditLogsPage;
